/** Cross-tab sync for favorite robot / facility / station ids. */

export const FAVORITES_STORAGE_KEY = 'fms.favorites.pins'
export const FAVORITES_BROADCAST_CHANNEL = 'fms.favorites.pins'

export type FavoritePinsSnapshot = {
  userId: number
  robot: number[]
  facility: number[]
  station: number[]
  organization: number[]
  updatedAt: number
}

export type FavoritePinsMessage = { type: 'favorites' } & FavoritePinsSnapshot

const idList = (raw: unknown): number[] => {
  if (!Array.isArray(raw)) return []
  return raw.map(Number).filter(value => Number.isInteger(value) && value > 0)
}

export function sameIdList(left: readonly number[], right: readonly number[]): boolean {
  if (left.length !== right.length) return false
  for (let index = 0; index < left.length; index += 1) {
    if (left[index] !== right[index]) return false
  }
  return true
}

export function sameFavoritePins(
  left: Pick<FavoritePinsSnapshot, 'robot' | 'facility' | 'station' | 'organization'>,
  right: Pick<FavoritePinsSnapshot, 'robot' | 'facility' | 'station' | 'organization'>
): boolean {
  return (
    sameIdList(left.robot, right.robot) &&
    sameIdList(left.facility, right.facility) &&
    sameIdList(left.station, right.station) &&
    sameIdList(left.organization, right.organization)
  )
}

export function parseFavoritePins(raw: unknown): FavoritePinsSnapshot | null {
  if (!raw || typeof raw !== 'object') return null
  const record = raw as Record<string, unknown>
  const userId = Number(record.userId)
  const updatedAt = Number(record.updatedAt)
  if (!Number.isInteger(userId) || userId <= 0) return null
  if (!Number.isFinite(updatedAt) || updatedAt <= 0) return null
  return {
    userId,
    robot: idList(record.robot),
    facility: idList(record.facility),
    station: idList(record.station),
    organization: idList(record.organization),
    updatedAt,
  }
}

export function readFavoritesFromStorage(): FavoritePinsSnapshot | null {
  try {
    const raw = localStorage.getItem(FAVORITES_STORAGE_KEY)
    if (!raw) return null
    return parseFavoritePins(JSON.parse(raw))
  } catch {
    return null
  }
}

export function writeFavoritesToStorage(snapshot: FavoritePinsSnapshot | null): void {
  try {
    if (!snapshot) {
      localStorage.removeItem(FAVORITES_STORAGE_KEY)
      return
    }
    localStorage.setItem(FAVORITES_STORAGE_KEY, JSON.stringify(snapshot))
  } catch {
    /* private mode / quota */
  }
}

export function publishFavoritePins(snapshot: FavoritePinsSnapshot): void {
  writeFavoritesToStorage(snapshot)
  try {
    if (typeof BroadcastChannel === 'undefined') return
    const channel = new BroadcastChannel(FAVORITES_BROADCAST_CHANNEL)
    const message: FavoritePinsMessage = { type: 'favorites', ...snapshot }
    channel.postMessage(message)
    channel.close()
  } catch {
    /* storage event still reaches other tabs */
  }
}

export function clearFavoritePinsStorage(): void {
  writeFavoritesToStorage(null)
}
